import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidPhoneNumber } from "libphonenumber-js";
import PhoneInput from "react-phone-number-input";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { staffAPI } from "services/staffService";

const AddStaffForm = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    status: "active",
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = "Invalid phone number";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      newErrors.password = "Password must contain lowercase letter";
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      newErrors.password = "Password must contain uppercase letter";
    } else if (!/(?=.*\d)/.test(formData.password)) {
      newErrors.password = "Password must contain number";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: undefined }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phone: value }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: undefined }));
    }
  };

  const handleStatusChange = (e) => {
    setFormData((prev) => ({ ...prev, status: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone,
        password: formData.password,
        status: formData.status,
      };

      await staffAPI.createStaff(payload);
      showToast("Staff member created successfully", "success");
      setTimeout(() => {
        navigate("/admin/staff-management");
      }, 1500);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Failed to create staff";
      showToast(message, "error");
      
      // Handle field-specific errors from backend
      if (err?.response?.data?.errors) {
        const fieldErrors = {};
        err.response.data.errors.forEach((errObj) => {
          if (errObj.path) {
            fieldErrors[errObj.path] = errObj.msg || "Invalid";
          }
        });
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/staff-management");
  };

  return (
    <div className="mt-3 flex h-full w-full flex-col gap-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Add Staff Member</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Create a new staff account and set permissions
        </p>
      </div>

      {/* Toast */}
      {toast.show && (
        <div
          className={`rounded-xl p-4 ${
            toast.type === "success"
              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Form Card */}
      <Card extra="p-6">
        <form onSubmit={handleSubmit} className="grid gap-6 md:grid-cols-2">
          {/* Name */}
          <div className="md:col-span-2">
            <InputField
              label="Full Name"
              id="name"
              type="text"
              placeholder="Enter staff member's full name"
              value={formData.name}
              onChange={handleChange}
              state={errors.name ? "error" : ""}
            />
            {errors.name && (
              <p className="mt-2 text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <InputField
              label="Email Address"
              id="email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={handleChange}
              state={errors.email ? "error" : ""}
            />
            {errors.email && (
              <p className="mt-2 text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Phone Number
            </label>
            <PhoneInput
              international
              countryCallingCodeEditable={true}
              defaultCountry="IN"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="Enter phone number"
              className={`mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none ${
                errors.phone
                  ? "border-red-500 dark:border-red-400"
                  : "border-gray-200 dark:border-white/10"
              }`}
            />
            {errors.phone && (
              <p className="mt-2 text-xs text-red-500">{errors.phone}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <InputField
              label="Password"
              id="password"
              type="password"
              placeholder="Minimum 8 characters with uppercase, lowercase, and number"
              value={formData.password}
              onChange={handleChange}
              state={errors.password ? "error" : ""}
            />
            {errors.password && (
              <p className="mt-2 text-xs text-red-500">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <InputField
              label="Confirm Password"
              id="confirmPassword"
              type="password"
              placeholder="Re-enter password"
              value={formData.confirmPassword}
              onChange={handleChange}
              state={errors.confirmPassword ? "error" : ""}
            />
            {errors.confirmPassword && (
              <p className="mt-2 text-xs text-red-500">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Status
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={handleStatusChange}
              className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none dark:border-white/10 dark:bg-navy-700 dark:text-white"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 md:col-span-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 rounded-xl border border-gray-200 py-3 font-medium text-navy-700 transition-all hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-white dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-xl bg-brand-500 py-3 font-medium text-white transition-all hover:bg-brand-600 disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-500"
            >
              {loading ? "Creating..." : "Create Staff Member"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddStaffForm;
